import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@/shared/assets/header/search.svg?react';
import { TableSkeleton } from '@/shared/components/ui/TableSkeleton';
import {
  SEARCH_CATEGORIES,
  SEARCH_HISTORY_DATE_FILTER,
  SEARCH_QUICK_OPTIONS,
  useGlobalSearchResults,
  useSearchHistoryResults,
  type SearchCategory,
} from '../model';
import { SearchScrollList } from './SearchScrollList';
import { SearchWalletsGroup } from './sections/SearchWalletsGroup';
import { SearchTradingGroups } from './sections/SearchTradingGroups';
import { SearchTradingTabs } from './sections/SearchTradingTabs';
import { SearchMarketsGroup } from './sections/SearchMarketsGroup';
import { SearchMarketsTabs } from './sections/SearchMarketsTabs';
import { SearchHistoryGroup } from './sections/SearchHistoryGroup';
import { SearchMoreButton } from './sections/SearchMoreButton';
import { SearchNotFound } from './SearchNotFound';

type Props = {
  query: string;
  category: SearchCategory;
  onQuickOptionSelect: (value: string) => void;
  onResetQuery: () => void;
  onClose: () => void;
};

const SearchInitialState = ({
  onQuickOptionSelect,
}: {
  onQuickOptionSelect: (value: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex grow flex-col items-center justify-center gap-[1.6rem] p-[2.4rem]">
      <div className="flex flex-col items-center gap-[0.8rem] text-center">
        <SearchIcon className="size-[3.2rem] text-primary" />
        <span className="headline4 text-primary">
          {t('personalAccount.globalSearch.empty.title')}
        </span>
        <span className="p4 text-secondary max-w-[36rem]">
          {t('personalAccount.globalSearch.empty.description')}
        </span>
      </div>

      <div className="flex items-center gap-[0.8rem]">
        {SEARCH_QUICK_OPTIONS.map(option => (
          <button
            key={option}
            type="button"
            onClick={() => onQuickOptionSelect(option)}
            className="h-[3.2rem] px-[1.6rem] rounded-full bg-secondary-bg medium6 text-primary cursor-pointer hover:bg-stroke transition-colors">
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

const GlobalSearchResults = ({
  query,
  category,
  onQuickOptionSelect,
  onResetQuery,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const results = useGlobalSearchResults(query);
  const history = useSearchHistoryResults(results.normalizedQuery);

  const hasQuery = results.normalizedQuery.length > 0;

  const openFullHistory = () => {
    onClose();
    navigate(`/history?datefilter=${SEARCH_HISTORY_DATE_FILTER}`);
  };

  const allHistoryButton = (
    <SearchMoreButton onClick={openFullHistory}>
      {t('personalAccount.globalSearch.allHistory')}
    </SearchMoreButton>
  );

  if (!hasQuery) {
    if (category === SEARCH_CATEGORIES.all) {
      return <SearchInitialState onQuickOptionSelect={onQuickOptionSelect} />;
    }

    if (category === SEARCH_CATEGORIES.wallets) {
      return (
        <SearchScrollList className="grow min-w-0">
          <SearchWalletsGroup
            rows={results.walletRows}
            accountNumber={results.walletsAccountNumber}
            baseCode={results.walletsBaseCode}
            withTabTitle
            onActionRun={onClose}
          />
        </SearchScrollList>
      );
    }

    if (category === SEARCH_CATEGORIES.tradingAccounts) {
      return (
        <SearchScrollList className="grow min-w-0">
          <SearchTradingTabs
            groups={results.tradingGroups}
            baseCode={results.tradingBaseCode}
            onActionRun={onClose}
          />
        </SearchScrollList>
      );
    }

    return (
      <div className="flex grow min-w-0 min-h-0 flex-col">
        {category === SEARCH_CATEGORIES.markets && (
          <SearchMarketsTabs onClose={onClose} />
        )}

        {category === SEARCH_CATEGORIES.history &&
          (history.isHistoryLoading ? (
            <TableSkeleton columns={2} rows={4} />
          ) : (
            <>
              <SearchScrollList className="flex-1">
                <SearchHistoryGroup
                  operations={history.operations}
                  statusIcons={history.statusIcons}
                  typeIcons={history.typeIcons}
                  withTitle={false}
                />
              </SearchScrollList>
              {allHistoryButton}
            </>
          ))}
      </div>
    );
  }

  const showWallets =
    category === SEARCH_CATEGORIES.all ||
    category === SEARCH_CATEGORIES.wallets;
  const showTrading =
    category === SEARCH_CATEGORIES.all ||
    category === SEARCH_CATEGORIES.tradingAccounts;
  const showMarkets =
    category === SEARCH_CATEGORIES.all ||
    category === SEARCH_CATEGORIES.markets;
  const showHistory =
    category === SEARCH_CATEGORIES.all ||
    category === SEARCH_CATEGORIES.history;

  const isLoading =
    (showMarkets && results.isMarketsLoading) ||
    (showHistory && history.isHistoryLoading);

  const hasResults =
    (showWallets && results.walletRows.length > 0) ||
    (showTrading && results.tradingGroups.length > 0) ||
    (showMarkets && results.marketAssets.length > 0) ||
    (showHistory && history.operations.length > 0);

  if (!hasResults && isLoading) {
    return (
      <div className="flex grow min-w-0 flex-col overflow-y-auto">
        <TableSkeleton columns={2} rows={4} />
      </div>
    );
  }

  if (!hasResults) {
    return <SearchNotFound onReset={onResetQuery} />;
  }

  return (
    <SearchScrollList className="grow min-w-0 gap-[1.6rem]">
      {showWallets && (
        <SearchWalletsGroup
          rows={results.walletRows}
          accountNumber={results.walletsAccountNumber}
          baseCode={results.walletsBaseCode}
          onActionRun={onClose}
        />
      )}

      {showTrading && (
        <SearchTradingGroups
          groups={results.tradingGroups}
          baseCode={results.tradingBaseCode}
          onActionRun={onClose}
        />
      )}

      {showMarkets && (
        <SearchMarketsGroup
          assets={results.marketAssets}
          baseAsset={results.marketsBaseAsset}
        />
      )}

      {showHistory && (
        <SearchHistoryGroup
          operations={history.operations}
          statusIcons={history.statusIcons}
          typeIcons={history.typeIcons}
          action={
            category === SEARCH_CATEGORIES.history
              ? allHistoryButton
              : undefined
          }
        />
      )}
    </SearchScrollList>
  );
};

export { GlobalSearchResults };
